import * as React from "react";

import { AccessSummaryEditChartColorsElementEnum } from "@app/access-reports/admin";
import { cloneDeep, isEqual } from "lodash";
import { ColorResult, SketchPicker } from "react-color";
import { FlyoutFooter } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardElementTypeEnum } from "@app/access-reports/shared/accessReportsWizardElementTypeEnum";
import { ByProductDefault } from "@app/access-reports/shared/byProductDefault";

interface AccessSummaryEditChartColorsProps {
  byProductDefault: ByProductDefault;
  disableButtons: boolean;
  elements: AccessReportsWizardElement[];
  hideSidePanel: () => void;
  onSaveAccessSummaryChartColorsClick: (byProductDefault: ByProductDefault) => void;
}

const AccessSummaryEditChartColors: React.FC<AccessSummaryEditChartColorsProps> = (props: AccessSummaryEditChartColorsProps) => {
  const [clonedByProductDefault, setClonedByProductDefault] = React.useState<ByProductDefault>(null);
  const [displayColorPicker, setDisplayColorPicker] = React.useState<AccessSummaryEditChartColorsElementEnum>(null);

  React.useEffect(() => {
    extractChartColorDefaults();
  }, [props.elements]);

  const buildAccessSummaryElement = (elementId: AccessSummaryEditChartColorsElementEnum): React.ReactNode => {
    const element: AccessReportsWizardElement = props.elements.find(x => x.elementId === elementId);
    const value: string = getValue(elementId);

    if (!!element) {
      switch (element.elementType) {
        case AccessReportsWizardElementTypeEnum.ColorPicker:
          const presetColors: string[] = element.options.map(x => x.color as string);

          return (
            <React.Fragment>
              <div className="col-md-6 d-flex align-items-center">
                <div className="color-picker-swatch" onClick={() => onColorPickerClick(element.elementId as AccessSummaryEditChartColorsElementEnum)}>
                  <div className="color-picker-color" style={{ backgroundColor: value || "#000" }} />
                </div>

                {(displayColorPicker === element.elementId) &&
                  <div className="color-picker-popover">
                    <div
                      className="color-picker-cover"
                      onClick={() => onColorPickerClose()}
                    />

                    <SketchPicker
                      color={value || "#000"}
                      disableAlpha={true}
                      presetColors={presetColors}
                      onChange={onColorPickerChange}
                    />
                  </div>
                }
              </div>
            </React.Fragment>
          );
        case AccessReportsWizardElementTypeEnum.Input:
          return (
            <React.Fragment>
              <div className="col-md-2">
                <QESTextInput
                  testId={`access-reports-admin__access-summary-chart-colors--${element.elementId}-input`}
                  disabled={props.disableButtons}
                  isValid={getInputValidity(element.elementId as AccessSummaryEditChartColorsElementEnum, value)}
                  label=""
                  onChange={(e) => onInputChange(element.elementId as AccessSummaryEditChartColorsElementEnum, e)}
                  unrequiredValidation={true}
                  value={value}
                />
              </div>
              <div className="col-md-4">
                <label className="label-s mb-0">
                  % Or Greater With Access
                </label>
              </div>
            </React.Fragment>
          );
        default:
          break;
      }
    }

    return (
      <div className="form-group mb-3">
        <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
        <span className="text-muted">
          Error
        </span>
      </div>
    );
  }

  const disableSaveButton = (): boolean => {
    if (props.disableButtons || isEqual(props.byProductDefault, clonedByProductDefault) || !clonedByProductDefault) {
      return true;
    }

    for (const bpd of Object.keys(clonedByProductDefault)) {
      if (clonedByProductDefault[bpd] === null || clonedByProductDefault[bpd] === undefined) {
        return true;
      }

      if (bpd === AccessSummaryEditChartColorsElementEnum.ChartPercentage1 ||
        bpd === AccessSummaryEditChartColorsElementEnum.ChartPercentage2 ||
        bpd === AccessSummaryEditChartColorsElementEnum.ChartPercentage3
      ) {
        const inputValidity: boolean = getInputValidity(bpd, clonedByProductDefault[bpd].toString());

        if (!inputValidity) {
          return true;
        }
      }
    }

    return false;
  }

  const extractChartColorDefaults = (): void => {
    if (!!props.byProductDefault) {
      const newClonedByProductDefault: ByProductDefault = cloneDeep(props.byProductDefault);

      setClonedByProductDefault(newClonedByProductDefault);
    }
  }

  const getInputValidity = (elementId: AccessSummaryEditChartColorsElementEnum, value: string): boolean => {
    if (!value) {
      return false;
    }

    const chartPercentageOneValue = parseInt(getValue(AccessSummaryEditChartColorsElementEnum.ChartPercentage1));
    const chartPercentageTwoValue = parseInt(getValue(AccessSummaryEditChartColorsElementEnum.ChartPercentage2));
    const chartPercentageThreeValue = parseInt(getValue(AccessSummaryEditChartColorsElementEnum.ChartPercentage3));

    const parsedValue: number = parseInt(value);

    if (elementId === AccessSummaryEditChartColorsElementEnum.ChartPercentage1) {
      if (parsedValue <= chartPercentageTwoValue || parsedValue <= chartPercentageThreeValue) {
        return false;
      }
    } else if (elementId === AccessSummaryEditChartColorsElementEnum.ChartPercentage2) {
      if (parsedValue >= chartPercentageOneValue || parsedValue <= chartPercentageThreeValue) {
        return false;
      }
    } else if (elementId === AccessSummaryEditChartColorsElementEnum.ChartPercentage3) {
      if (parsedValue >= chartPercentageOneValue || parsedValue >= chartPercentageTwoValue) {
        return false;
      }
    }

    return true;
  }

  const getValue = (elementId: AccessSummaryEditChartColorsElementEnum): string => {
    if (!!clonedByProductDefault) {
      return clonedByProductDefault[elementId] as string;
    }

    return "";
  }

  const onInputChange = (elementId: AccessSummaryEditChartColorsElementEnum, event: React.ChangeEvent<HTMLInputElement>): void => {
    const value: number = parseInt(event.target.value);

    if ((!isNaN(value) && value > 0 && value < 100) || !event.target.value) {
      const newClonedByProductDefault: ByProductDefault = { ...clonedByProductDefault };
      newClonedByProductDefault[elementId] = !event.target.value ? event.target.value : value;

      setClonedByProductDefault(newClonedByProductDefault);
    }
  }

  const onColorPickerClick = (accessSummaryElementId: AccessSummaryEditChartColorsElementEnum): void => {
    if (accessSummaryElementId === displayColorPicker) {
      setDisplayColorPicker(null);
    } else {
      setDisplayColorPicker(accessSummaryElementId);
    }
  }

  const onColorPickerChange = (color: ColorResult): void => {
    const newClonedByProductDefault: ByProductDefault = { ...clonedByProductDefault };
    newClonedByProductDefault[displayColorPicker] = color.hex;

    setClonedByProductDefault(newClonedByProductDefault);
  }

  const onColorPickerClose = (): void => {
    setDisplayColorPicker(null);
  }

  return (
    <React.Fragment>
      <div className="row align-items-center mb-3">
        {buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage1)}
        {buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage1Color)}
      </div>

      <div className="row align-items-center mb-3">
        {buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage2)}
        {buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage2Color)}
      </div>

      <div className="row align-items-center mb-3">
        {buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage3)}
        {buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage3Color)}
      </div>

      <div className="row align-items-center mb-3">
        <div className="offset-md-2 col-md-4">
          <label className="label-s mb-0">
            Less than {getValue(AccessSummaryEditChartColorsElementEnum.ChartPercentage3)}% With Access
          </label>
        </div>
        {buildAccessSummaryElement(AccessSummaryEditChartColorsElementEnum.ChartPercentage4Color)}
      </div>

      <FlyoutFooter
        submitButton={
          <QESButton
            disabled={disableSaveButton()}
            onClick={() => props.onSaveAccessSummaryChartColorsClick(clonedByProductDefault)}
            qesTestId="access-reports-admin__access-summary-chart-colors--save"
            qesType="primary"
          >
            Save
          </QESButton>
        }
      />
    </React.Fragment>
  );
}

export { AccessSummaryEditChartColors };
